import React, { useState } from "react";
import { Link2, Copy, Loader2, MessageCircle, Mail, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Textarea } from "../ui/textarea";
import { teamApi } from "../../lib/api";

/**
 * Generates a public pre-fitment sign-off link for the client. The fitter can
 * normally share this from the team app, but the admin can also share it
 * directly from the job detail when the customer prefers to sign before arrival.
 */
export const PreFitmentShareCard = ({ job, onShared }) => {
  const [busy, setBusy] = useState(false);
  const [link, setLink] = useState(null);
  const [notes, setNotes] = useState("");
  const existingSnap = job?.prefitment_snapshot;
  const existingToken = existingSnap?.token;
  const existingUrl = existingToken
    ? `${window.location.origin}/sign/${existingToken}`
    : null;
  const signed = Boolean(existingSnap?.client_signature);

  const share = async () => {
    setBusy(true);
    try {
      const res = await teamApi.sharePrefitment(job.id, {
        notes: notes.trim() || null,
        shared_by: "admin",
        photos: existingSnap?.photos || [],
        damages: existingSnap?.damages || [],
      });
      const url = `${window.location.origin}/sign/${res.token}`;
      setLink(url);
      toast.success("Pre-fitment link generated");
      onShared?.();
    } catch {
      toast.error("Could not generate link");
    } finally { setBusy(false); }
  };

  const activeUrl = link || existingUrl;

  const copy = async () => {
    if (!activeUrl) return;
    try {
      await navigator.clipboard.writeText(activeUrl);
      toast.success("Link copied");
    } catch {
      toast.error("Could not copy");
    }
  };

  const waMessage = encodeURIComponent(
    `Hi ${job.client_name || "there"}, please review the pre-fitment details and sign before your ${job.fitment_date || "scheduled"} appointment:\n\n${activeUrl || ""}`
  );
  const emailSubject = encodeURIComponent(`Pre-fitment sign-off — Job #${job.job_number}`);
  const emailBody = encodeURIComponent(
    `Hi ${job.client_name || "there"},\n\nPlease review the pre-fitment details and sign before your appointment:\n${activeUrl || ""}\n\nThank you,\nDiesel & Dust Group`
  );

  return (
    <div className="bg-white border border-[#cfd7db] rounded-md p-5" data-testid="prefitment-share-card">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="font-display uppercase tracking-widest text-xs text-[#f28a16]">Pre-fitment sign-off</div>
          <div className="font-display font-bold text-[#111619] mt-0.5">Send the client a digital pre-acceptance link</div>
        </div>
        {signed && (
          <span className="inline-flex items-center gap-1.5 bg-[#10b981] text-white text-xs font-display uppercase tracking-widest px-2.5 py-1 rounded-sm">
            <CheckCircle2 size={12} /> Signed by client
          </span>
        )}
      </div>

      {!activeUrl && (
        <div className="mt-3 space-y-3">
          <p className="text-sm text-[#3f4b52]">
            Generates a unique signing page where the client confirms vehicle details, photos and accepts the T&amp;Cs.
            The signature is stored against this job.
          </p>
          <Textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            placeholder="Optional note for the client (e.g. arrival window)"
            className="bg-white border-[#cfd7db]"
            data-testid="prefitment-share-notes"
          />
          <button
            type="button"
            onClick={share}
            disabled={busy}
            data-testid="prefitment-share-btn"
            className="bg-[#f28a16] text-white px-4 h-10 rounded-md font-display uppercase tracking-widest text-xs inline-flex items-center gap-2 hover:brightness-110 disabled:opacity-60"
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Link2 size={14} />}
            {busy ? "Generating…" : "Generate sign-off link"}
          </button>
        </div>
      )}

      {activeUrl && (
        <div className="mt-3 space-y-3">
          <div className="bg-[#f5f6f6] border border-[#cfd7db] rounded-md p-2.5 text-xs text-[#111619] break-all font-mono" data-testid="prefitment-share-url">
            {activeUrl}
          </div>
          <div className="flex flex-wrap gap-2">
            <button onClick={copy} className="border border-[#cfd7db] text-[#111619] px-3 h-9 rounded-md font-display uppercase tracking-widest text-[11px] inline-flex items-center gap-1.5 hover:bg-[#edf2f4]" data-testid="prefitment-share-copy">
              <Copy size={12} /> Copy
            </button>
            {job.contact_mobile && (
              <a
                href={`https://wa.me/${job.contact_mobile.replace(/\D/g, "")}?text=${waMessage}`}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-[#25d366] text-white px-3 h-9 rounded-md font-display uppercase tracking-widest text-[11px] inline-flex items-center gap-1.5"
              >
                <MessageCircle size={12} /> WhatsApp
              </a>
            )}
            {job.email && (
              <a
                href={`mailto:${job.email}?subject=${emailSubject}&body=${emailBody}`}
                className="bg-[#20282c] text-white px-3 h-9 rounded-md font-display uppercase tracking-widest text-[11px] inline-flex items-center gap-1.5"
              >
                <Mail size={12} /> Email
              </a>
            )}
            <button
              type="button"
              onClick={() => { setLink(null); setNotes(""); }}
              className="text-[#3f4b52] text-xs hover:underline ml-auto"
            >
              Re-generate
            </button>
          </div>
          {existingSnap?.shared_at && !link && (
            <div className="text-xs text-[#3f4b52]">
              First shared {new Date(existingSnap.shared_at).toLocaleString("en-ZA")}
              {signed && existingSnap.signed_at && ` · Signed ${new Date(existingSnap.signed_at).toLocaleString("en-ZA")}`}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PreFitmentShareCard;
